import { PriceDisplay } from "./PriceDisplay";
import type { OptionPriceInfo, PlanPrice } from "./types";

// オプション検査の共通ヘッダー（名前・説明・補助対象バッジ）
const OptionRowHeader = ({
  label,
  description,
  hasSubsidy,
  fiscalYear,
}: {
  label: string;
  description: string;
  hasSubsidy?: boolean;
  fiscalYear?: number;
}) => (
  <div className="flex flex-col gap-1 md:grid md:grid-cols-[280px_1fr] md:gap-x-4">
    <div className="flex flex-col gap-1">
      <h3 className="text-base font-bold text-primary-6">{label}</h3>
      {hasSubsidy && fiscalYear != null && (
        <span className="w-fit rounded-md border border-primary-6 px-1 py-0.5 text-xs font-bold text-primary-6">
          {fiscalYear}年度補助対象
        </span>
      )}
    </div>
    <p className="text-sm text-monotone-7">{description}</p>
  </div>
);

// 単一価格の表示
const SinglePriceSection = ({
  selfPayAmount,
  counterPayAmount,
}: {
  selfPayAmount: number;
  counterPayAmount: number;
}) => (
  <div className="flex justify-end">
    <PriceDisplay
      counterPayAmount={counterPayAmount}
      selfPayAmount={selfPayAmount}
    />
  </div>
);

// コース別価格の一覧表示
const PlanPricesSection = ({ planPrices }: { planPrices: PlanPrice[] }) => (
  <div className="space-y-2">
    {planPrices.map((planPrice) => (
      <div
        key={planPrice.planCode}
        className="flex flex-col gap-2 md:flex-row md:items-center md:py-2"
      >
        {/* スマホ: コース名と金額を縦並び、PC: 横並びで線を引く */}
        <div className="shrink-0 text-sm font-bold text-monotone-7">
          {planPrice.planLabel}
        </div>
        <div className="mx-4 hidden h-px flex-1 bg-monotone-3 md:block" />
        <div className="flex justify-end md:justify-start">
          <div className="flex shrink-0 items-center gap-4">
            <PriceDisplay
              counterPayAmount={planPrice.counterPayAmount}
              selfPayAmount={planPrice.selfPayAmount}
              size="sm"
            />
          </div>
        </div>
      </div>
    ))}
  </div>
);

/**
 * オプション検査行コンポーネント
 * priceInfo で価格表示パターンを明確に分離：
 * - { kind: "single" }: 単一価格
 * - { kind: "perPlan" }: コース別価格
 * - null: 価格なし
 */
export const OptionRow = ({
  label,
  description,
  hasSubsidy,
  fiscalYear,
  priceInfo,
}: {
  label: string;
  description: string;
  hasSubsidy?: boolean;
  fiscalYear?: number;
  priceInfo: OptionPriceInfo;
}) => (
  <div className="border-b border-monotone-2 py-4 last:border-b-0">
    <div className="flex flex-col gap-2">
      <OptionRowHeader
        label={label}
        description={description}
        hasSubsidy={hasSubsidy}
        fiscalYear={fiscalYear}
      />
      {priceInfo?.kind === "single" && (
        <SinglePriceSection
          selfPayAmount={priceInfo.selfPayAmount}
          counterPayAmount={priceInfo.counterPayAmount}
        />
      )}
      {priceInfo?.kind === "perPlan" && (
        <PlanPricesSection planPrices={priceInfo.planPrices} />
      )}
    </div>
  </div>
);
